import { BookingSplitRow, Report, ReportMetadata, ServiceItem, ServiceTransaction } from '../types';
import { isHourlyUnit } from './projectLv';
import { FlatRedirect, ReportBookingOpts, bookedReportNumbers, redirectMainBookingsToFlat, resolveMainBookings } from './reportBooking';
import { buchungstag, isAccountingEntry } from './serviceLogUtils';
import { bookingIsFlatProof, inspectionFlatTarget, isFlatRateService, resolveFlatCoverage } from './flatRateLogic';
import { isNepService } from './nepPosition';
import { UnbookedSentReportRow, isInspectionBilled } from './unbookedReportsLogic';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BEGEHUNG NACHBUCHEN — die reine Logik
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Gebucht wird in dieser Anwendung ausschliesslich BEIM VERSENDEN
 * (`applyReportBookingWithNotices`, gerufen aus EmailModal und aus dem
 * Wiederaufnahme-Pfad). Faellt die Buchung dort aus — kein Buchungsziel
 * gewaehlt, Absturz zwischen Mail und Speichern, Versand ueber einen anderen
 * Weg — dann gibt es bis heute KEINEN Weg zurueck: ein erneuter Versand bucht
 * nicht nach (`isExported` sperrt die Abrechnung), und die Leistungserfassung
 * schreibt eine formlose Zeile ohne `reportRef`, die jede spaetere Pruefung
 * uebersieht.
 *
 * Dieses Modul bereitet den Vorgang vor. Es SCHREIBT NICHTS und entscheidet
 * nichts allein: es sagt, ob nachgebucht werden darf, auf welche Position, mit
 * welcher Menge — und der Mensch bestaetigt.
 *
 * ─── Die Leitplanke ────────────────────────────────────────────────────────
 * Der teuerste Fehler ist nicht die vergessene Buchung, sondern die zweite.
 * Eine fehlende Begehung kostet eine Nachforderung; eine doppelte kostet eine
 * falsche Rechnung beim Kunden und eine Korrektur nur ueber Storno. Deshalb
 * gilt hier durchgehend: im Zweifel NICHT buchen und den Zweifel anzeigen.
 */

/** Woher der Vorschlag fuer die Zielposition stammt — gehoert in den Dialog. */
export type RebookTargetSource =
    /** Aus dem archivierten Bericht selbst (`currentBillingServiceId`/`-Split`). */
    | 'archiv'
    /** Aus der Buchung der benachbarten Berichte desselben Projekts. */
    | 'nachbarschaft'
    /** Einzige Begehungsposition des Projekts. */
    | 'einzige_begehungsposition'
    /** Pauschalprojekt: die Begehung geht als Nachweis auf die Monatspauschale. */
    | 'monatspauschale'
    /** Kein belastbarer Vorschlag — der Bearbeiter muss waehlen. */
    | 'keiner';

export interface RebookTargetProposal {
    /** Der Vorschlag; leer, wenn `source === 'keiner'`. */
    bookings: BookingSplitRow[];
    source: RebookTargetSource;
    /** Klartext fuer den Dialog, immer gesetzt. */
    begruendung: string;
}

/** Ein Grund, warum gerade NICHT nachgebucht werden darf. */
export type RebookBlockerReason =
    | 'schreibgeschuetzt'
    | 'bereits_gebucht'
    | 'kein_buchungsziel'
    | 'ausgenommen'
    | 'kein_archiv';

export interface RebookBlocker {
    reason: RebookBlockerReason;
    message: string;
}

/** Ein Hinweis, der das Nachbuchen NICHT verhindert, aber gelesen werden muss. */
export interface RebookWarning {
    /** Kurzform fuer die Sortierung/Tests. */
    kind: 'abgerechneter_zeitraum' | 'abrechnungslage_unbekannt' | 'tagesriegel' | 'tochter' | 'nur_zusatzleistung' | 'menge_ungewoehnlich' | 'nennung_ausserhalb_fenster';
    message: string;
}

/**
 * Steht im Protokoll eine Zeile, die GENAU DIESE Berichtsnummer nennt, aber
 * zu weit vom Begehungstag entfernt liegt, um als Buchung zu zaehlen?
 *
 * Das ist die Luecke der Pruefung, und sie ist bewusst so: das Fenster von
 * dreissig Tagen verhindert, dass eine Zeile „Bericht Nr. 44" aus einem
 * anderen Jahr eine offene Forderung wegraeumt. Der Preis dafuer ist, dass
 * eine spaet nachgetragene Alt-Buchung durchrutscht und der Bericht weiter
 * als offen gemeldet wird — Projekt 5301, Bericht #21: die Buchung
 * „Bericht #21 versendet (Cloud)" steht dreiundvierzig Tage nach der Begehung
 * im Protokoll.
 *
 * Die Pruefung darf deshalb nicht aufgeweicht werden. Aber der Bearbeiter, der
 * gleich auf „Buchen" drueckt, MUSS diese Zeile sehen: sie ist mit grosser
 * Wahrscheinlichkeit genau die Buchung, die er gerade ein zweites Mal anlegt.
 */
export const nennungAusserhalbFenster = (
    serviceLog: ServiceTransaction[] | undefined,
    inspectionNumber: number,
    begehungstag: string | null,
): RebookWarning | null => {
    if (!begehungstag) return null;
    const nennt = new RegExp(`(#|\\bNr\\.?\\s*)${inspectionNumber}(?![0-9]|\\.[0-9])`);
    const treffer = (serviceLog || []).find(e => {
        if (!e || !(Number(e.quantity) > 0)) return false;
        const desc = String(e.description || '');
        if (desc.startsWith('Zusatzleistung')) return false;
        // Buchhaltungsvorgaenge sind keine Leistungsbuchungen. BEWUSST
        // `isAccountingEntry` (nur die Praefixe BU-/VZ-/RN-/MB-) und NICHT
        // `isPostingEntry`: dessen Textzweig („manuelle Verbuchung") und die
        // Journal-Buchungsart wuerden hier zusaetzliche Zeilen ausblenden —
        // und genau diese Zeilen soll der Bearbeiter SEHEN, bevor er ein
        // zweites Mal bucht. Der Hinweis darf enger werden, nie stiller.
        if (isAccountingEntry(e)) return false;
        if (!nennt.test(desc)) return false;
        const tag = archivTag(e.date);
        if (!tag) return false;
        const abstand = Math.abs(
            (new Date(`${tag}T00:00:00`).getTime() - new Date(`${begehungstag}T00:00:00`).getTime()) / 86_400_000,
        );
        return abstand > 30;
    });
    if (!treffer) return null;
    return {
        kind: 'nennung_ausserhalb_fenster',
        message:
            `Im Protokoll steht am ${tagDeutsch(treffer.date)} bereits „${treffer.description}" `
            + `mit Menge ${treffer.quantity}. Der Eintrag nennt genau diesen Bericht, liegt aber mehr als `
            + '30 Tage vom Begehungstag entfernt — deshalb erkennt ihn die Prüfung nicht als Buchung und '
            + 'meldet den Bericht weiter als offen. Sehr wahrscheinlich IST das die Buchung. Bitte prüfen: '
            + 'dann gehört hier „nicht abrechenbar" hin und keine zweite Buchung.',
    };
};

/** Was der Dialog vor dem Buchen zeigt — Auflage: nichts davon darf fehlen. */
export interface RebookPreview {
    projektNummer: string | null;
    projektName: string | null;
    berichtsNummer: number;
    /** Kalendertag der Begehung, `YYYY-MM-DD`. */
    begehungstag: string | null;
    /** Uhrzeit `HH:MM`, notfalls der Ersatzwert. */
    uhrzeit: string;
    /** Gesetzt, wenn die Buchung ins Protokoll der MUTTER geht. */
    mutterProjektId: string | null;
    /**
     * `nachweis`: die Zeile liegt auf einer Monatspauschale und wird eine
     * Nachweiszeile (Menge 0, NW-…) — `menge` ist dann nur das Buchungssignal
     * und darf nicht als Menge angezeigt werden.
     */
    zeilen: Array<{ serviceId: string; titel: string; lvPosition: string; menge: number; einheit: string; nachweis?: boolean }>;
    blocker: RebookBlocker[];
    warnungen: RebookWarning[];
}

/**
 * Ersatz-Uhrzeit, wenn das Archiv keine kennt.
 *
 * 08:00 ORTSZEIT und nicht 00:00: aus Mitternacht wird beim Umrechnen nach UTC
 * der Vortag (in Deutschland ganzjaehrig), und der Buchungseintrag landete auf
 * dem falschen Kalendertag — genau dort, wo `same_day_same_section` und die
 * Zeitraumauswahl der Rechnung ihn suchen.
 */
export const ERSATZ_UHRZEIT = '08:00';

/**
 * Beschreibung und Ref der Kind-zu-Mutter-Buchung.
 *
 * Woertlich die Form aus SiGeKoInspectionModule — die Sicht
 * `v_unbooked_sent_reports` erkennt genau diesen Ref, und die Mengenklammer
 * am Ende ist der einzige Ort, an dem im Mutterprotokoll steht, wie viel fuer
 * die Tochter gebucht wurde.
 */
// Die Definition liegt in serviceLogUtils (importfrei): der Verknuepfungs-
// Transfer in projectLv schreibt dieselbe Form, und projectLv wird von dieser
// Datei importiert — von hier aus waere es ein Zyklus. Importiert UND wieder
// exportiert: ein blosses `export … from` bindet den Namen nicht lokal, und
// `buildRebookPreview` unten braucht ihn; die Aufrufer (Nachbuchen,
// Sicht-Abgleich) lesen weiter von hier.
import { childBookingRef, childBookingDescription } from './serviceLogUtils';
export { childBookingRef, childBookingDescription };

/**
 * Der Begehungstag als Kalendertag — seit S5d in Europe/Berlin (`buchungstag`),
 * derselben Zone wie der Tagesriegel, die Journal-Spalte `entry_day` und der
 * Schreibpfad (`berlinZeitpunkt`).
 *
 * `report_history.date` ist eine echte `date`-Spalte. Ueber PostgREST kommt sie
 * als `'YYYY-MM-DD'` an und wird unveraendert uebernommen. Kommt sie als voller
 * Zeitstempel (Treiber, die `date` in ein Date-Objekt wandeln), darf NICHT die
 * ISO-Zeichenkette abgeschnitten werden: aus lokaler Mitternacht wird `23:00Z`
 * des Vortages, und der Bericht wuerde einen Tag zu frueh gebucht. Bis S5d
 * rechnete diese Stelle dafuer in Geraetezeit — im Inland dasselbe; auf einem
 * Geraet in anderer Zone lagen Begehungstag und vorhandene Buchung (seit S5d
 * in Berlin gelesen) an verschiedenen Tagen.
 */
export const archivTag = (value?: string | null): string | null => buchungstag(value);

/** `HH:MM` aus dem Archiv, sonst der Ersatzwert. */
export const archivUhrzeit = (snapshot?: Report | null, inspectionTime?: string | null): string => {
    const ausSpalte = (inspectionTime || '').slice(0, 5);
    if (/^\d{2}:\d{2}$/.test(ausSpalte)) return ausSpalte;
    const ausSnapshot = String(snapshot?.metadata?.time || '').slice(0, 5);
    if (/^\d{2}:\d{2}$/.test(ausSnapshot)) return ausSnapshot;
    return ERSATZ_UHRZEIT;
};

/**
 * Die Zielposition VORSCHLAGEN — nie stillschweigend festlegen.
 *
 * Reihenfolge nach Belastbarkeit. Der archivierte Bericht weiss selbst, was
 * beim Versand haette gebucht werden sollen; das ist die einzige Quelle, die
 * nicht raet. Alles danach ist ein Indiz und wird im Dialog auch so benannt.
 */
export const proposeRebookTarget = (
    snapshot: Report | null | undefined,
    services: ServiceItem[],
    serviceLog: ServiceTransaction[] | undefined,
): RebookTargetProposal => {
    const basis = proposeRebookTargetOhnePauschale(snapshot, services, serviceLog);

    // Pauschalprojekt: im Pauschalprojekt bekommt eine GEDECKTE
    // Begehungsposition nichts mehr (Betreiber 30.09.2026), die Begehung geht
    // als Nachweis auf die Pauschale — dieselbe Regel wie beim Versand
    // (redirectMainBookingsToFlat). Der archivierte Bericht nennt oft noch
    // 2.7.10 aus der Zeit vor der Pauschale; der wird umgelenkt.
    //
    // Eine NICHT gedeckte Position bleibt, was sie ist (Codex-Befund 30.09.):
    // deckt eine 'explicit'-Pauschale nur Los A und gehoert der Bericht zu
    // Los B, waere ein Nachweis mit Menge 0 statt der abrechenbaren Menge auf
    // B eine fehlende Forderung.
    const pauschale = inspectionFlatTarget(services.filter(s => !isNepService(s)));
    if (!pauschale) return basis;

    const alsPauschale = (bookings: BookingSplitRow[]): RebookTargetProposal => ({
        bookings,
        source: 'monatspauschale',
        begruendung: 'Pauschalprojekt: die Begehung wird als Nachweis auf die Monatspauschale gebucht – der Monat ändert sich nicht, abgerechnet wird nur der Monat.',
    });

    if (basis.bookings.length === 0) {
        // Ohne Anhaltspunkt: die Pauschale — ausser es gibt daneben eine
        // Begehungsposition, die sie NICHT deckt. Dann ist die Frage offen und
        // bleibt es; der Bearbeiter waehlt.
        const deckung = resolveFlatCoverage(services);
        const ungedeckt = services.some(s =>
            s.assignment === 'inspection' && !isNepService(s) && !isFlatRateService(s) && deckung.get(s.id) !== pauschale.id);
        return ungedeckt ? basis : alsPauschale([{ serviceId: pauschale.id, quantity: 1 }]);
    }

    const { rows, redirectedToFlat } = redirectMainBookingsToFlat(basis.bookings, services);
    const aufPauschale = redirectedToFlat || rows.some(r => r.serviceId === pauschale.id);
    return aufPauschale ? alsPauschale(rows) : basis;
};

/** Der Vorschlag ohne Blick auf eine Monatspauschale — Archiv, Nachbarschaft, einzige Position. */
const proposeRebookTargetOhnePauschale = (
    snapshot: Report | null | undefined,
    services: ServiceItem[],
    serviceLog: ServiceTransaction[] | undefined,
): RebookTargetProposal => {
    const vorhanden = (id?: string | null) => !!id && services.some(s => s.id === id);

    // (1) Der Bericht selbst.
    const meta = snapshot?.metadata as ReportMetadata | undefined;
    const split = (meta?.currentBillingSplit || []).filter(r => r?.serviceId && (r.quantity ?? 0) > 0 && vorhanden(r.serviceId));
    if (split.length > 0) {
        return {
            bookings: split.map(r => ({ serviceId: r.serviceId, quantity: r.quantity })),
            source: 'archiv',
            begruendung: split.length > 1
                ? 'Aus dem archivierten Bericht: er war beim Versand auf mehrere Lose aufgeteilt.'
                : 'Aus dem archivierten Bericht — genau die Position, die beim Versand abgerechnet werden sollte.',
        };
    }
    if (vorhanden(meta?.currentBillingServiceId)) {
        return {
            bookings: [{ serviceId: meta!.currentBillingServiceId!, quantity: 1 }],
            source: 'archiv',
            begruendung: 'Aus dem archivierten Bericht — genau die Position, die beim Versand abgerechnet werden sollte.',
        };
    }

    // (2) Die Nachbarschaft: worauf haben die anderen Berichte dieses Projekts
    //     gebucht? Eine Mehrheit unter den Hauptbuchungen ist ein starkes
    //     Indiz, aber eben nur ein Indiz — deshalb eigene Quelle.
    const zaehler = new Map<string, number>();
    for (const e of serviceLog || []) {
        if (!e?.serviceId || !vorhanden(e.serviceId)) continue;
        const istHaupt = /^Bericht #\d+ abgeschlossen$/.test(String(e.description || ''))
            || (typeof e.reportRef === 'string' && e.reportRef.startsWith('RPT-') && !String(e.description || '').startsWith('Zusatzleistung'));
        if (!istHaupt) continue;
        zaehler.set(e.serviceId, (zaehler.get(e.serviceId) || 0) + 1);
    }
    if (zaehler.size === 1) {
        const [serviceId, anzahl] = [...zaehler.entries()][0];
        return {
            bookings: [{ serviceId, quantity: 1 }],
            source: 'nachbarschaft',
            begruendung: `Vorschlag: alle ${anzahl} bisher gebuchten Berichte dieses Projekts liegen auf dieser Position.`,
        };
    }

    // Buchen die Nachbarberichte auf VERSCHIEDENE Positionen, ist die Frage
    // offen — und bleibt es. Hier auf „es gibt ja nur eine Begehungsposition"
    // auszuweichen hiesse, ein Projekt mit erkennbar gemischter Zuordnung
    // trotzdem in eine Spur zu zwingen; genau daraus entstehen die
    // Fehlzuordnungen, die spaeter niemand mehr erklaeren kann.
    if (zaehler.size > 1) {
        return {
            bookings: [],
            source: 'keiner',
            begruendung: 'Kein Vorschlag: die bisherigen Berichte dieses Projekts buchen auf verschiedene Positionen. Bitte die Position selbst wählen.',
        };
    }

    // (3) Genau eine Begehungsposition — dann gibt es nichts zu waehlen.
    const begehungen = services.filter(s => s.assignment === 'inspection');
    if (begehungen.length === 1) {
        return {
            bookings: [{ serviceId: begehungen[0].id, quantity: 1 }],
            source: 'einzige_begehungsposition',
            begruendung: 'Vorschlag: es gibt genau eine Begehungsposition im Leistungsverzeichnis.',
        };
    }

    return {
        bookings: [],
        source: 'keiner',
        begruendung: zaehler.size > 1
            ? 'Kein Vorschlag: die bisherigen Berichte dieses Projekts buchen auf verschiedene Positionen. Bitte die Position selbst wählen.'
            : 'Kein Vorschlag: der archivierte Bericht nennt keine Abrechnungsposition. Bitte die Position selbst wählen.',
    };
};

/**
 * Die Buchungsanweisung. Bewusst dieselbe Struktur wie beim Versand, damit
 * `applyReportBookingWithNotices` NICHT unterscheiden kann, woher der Vorgang
 * kommt — jede Sonderbehandlung waere eine zweite Buchungsregel.
 *
 * `additionalAddons` bleibt leer: Zusatzleistungen wurden beim Versand bereits
 * gebucht (`nur_zusatzleistung_gebucht` weist genau darauf hin). Sie hier
 * erneut zu buchen waere die Doppelbuchung, die dieses Werkzeug verhindern
 * soll.
 */
export const rebookOpts = (args: {
    row: UnbookedSentReportRow;
    bookings: BookingSplitRow[];
    snapshot?: Report | null;
    inspectionTime?: string | null;
    /** Der Durchfuehrende der BEGEHUNG, nicht der Bearbeiter von heute. */
    performerId: string;
    performerName: string;
}): ReportBookingOpts => ({
    mainBookings: args.bookings,
    serviceId: args.bookings.length === 1 ? args.bookings[0].serviceId : null,
    hours: args.bookings.length === 1 ? args.bookings[0].quantity : null,
    additionalAddons: {},
    performerId: args.performerId,
    performerName: args.performerName,
    inspectionNumber: args.row.inspection_number,
    sectionId: args.row.section_id,
    date: archivTag(args.row.inspection_date) ?? undefined,
    time: archivUhrzeit(args.snapshot, args.inspectionTime),
});

/**
 * Liegt der Begehungstag in einem bereits abgerechneten Zeitraum?
 *
 * Das ist der wichtigste Hinweis des ganzen Dialogs. Ist der Zeitraum gestellt,
 * wurde die Leistung mit grosser Wahrscheinlichkeit ANDERS abgerechnet — unter
 * einer anderen Berichtsnummer, in einem anderen Projekt, als Sammelposition.
 * Dann ist die richtige Antwort „nicht abrechenbar" mit Begruendung, nicht
 * eine zweite Buchung auf einen Beleg, der schon beim Kunden liegt.
 */
export const zeitraumWarnung = (
    abrechnung: { stand?: string; beleg?: string; von?: string; bis?: string } | null | undefined,
): RebookWarning | null => {
    if (!abrechnung || abrechnung.stand !== 'abgerechnet') return null;
    const beleg = abrechnung.beleg ? ` (${abrechnung.beleg})` : '';
    const zeitraum = abrechnung.von && abrechnung.bis
        ? ` vom ${tagDeutsch(abrechnung.von)} bis ${tagDeutsch(abrechnung.bis)}`
        : '';
    return {
        kind: 'abgerechneter_zeitraum',
        message:
            `Der Begehungstag liegt in einem bereits abgerechneten Zeitraum${zeitraum}${beleg}. `
            + 'Sehr wahrscheinlich wurde diese Leistung anders abgerechnet — dann ist „nicht abrechenbar" '
            + 'mit Begründung richtig und diese Buchung wäre eine Doppelforderung. Nur nachbuchen, wenn '
            + 'am Beleg nachgewiesen ist, dass die Begehung dort FEHLT.',
    };
};

const tagDeutsch = (iso?: string | null): string => {
    const tag = archivTag(iso);
    if (!tag) return 'unbekannt';
    const [y, m, d] = tag.split('-');
    return `${d}.${m}.${y}`;
};

/**
 * Die vollstaendige Vorschau — alles, was der Dialog zeigt, entsteht hier.
 *
 * `serviceLog`/`services` MUESSEN aus dem frisch geladenen BUCHUNGSPROJEKT
 * stammen (bei einer Tochter also aus der Mutter). Gegen den Client-Zustand zu
 * pruefen hiesse, gegen einen Stand zu pruefen, der beim Speichern gar nicht
 * mehr gilt.
 */
export const buildRebookPreview = (args: {
    row: UnbookedSentReportRow;
    /** Positionen des Buchungsprojekts. */
    services: ServiceItem[];
    /** Protokoll des Buchungsprojekts. */
    serviceLog: ServiceTransaction[] | undefined;
    bookings: BookingSplitRow[];
    snapshot?: Report | null;
    inspectionTime?: string | null;
    istSchreibgeschuetzt?: boolean;
    sperrHalter?: string | null;
    abrechnung?: { stand?: string; beleg?: string; von?: string; bis?: string } | null;
    /** Fehlt der Archiveintrag, gibt es keinen Begehungstag — dann geht nichts. */
    archivFehlt?: boolean;
    /**
     * `linkedProjectConfig.billingMode === 'daily_flat'` am Kind. Aendert NUR
     * den Wortlaut der Tochter-Warnung: bei Einzelabrechnung ist derselbe
     * Begehungstag bei Mutter und Tochter der Normalfall.
     */
    tagespauschale?: boolean;
}): RebookPreview => {
    const { row, services, serviceLog } = args;
    const tag = archivTag(row.inspection_date);
    const blocker: RebookBlocker[] = [];
    const warnungen: RebookWarning[] = [];

    if (args.istSchreibgeschuetzt) {
        blocker.push({
            reason: 'schreibgeschuetzt',
            message: args.sperrHalter
                ? `${args.sperrHalter} bearbeitet dieses Projekt gerade — es wurde nichts gebucht.`
                : 'Das Projekt ist schreibgeschützt geöffnet — es wurde nichts gebucht.',
        });
    }
    if (row.report_exempt) {
        // Nur ein Hinweis waere zu wenig: der Haken sagt „dieses Projekt fuehrt
        // keine Berichte". Ein Bericht, der beim Kunden ist, widerspricht dem.
        warnungen.push({
            kind: 'nur_zusatzleistung',
            message: 'Für dieses Projekt ist „keine Begehungen/Berichte" gesetzt — der Bericht ist trotzdem beim Kunden. Bitte vorher klären, welche der beiden Angaben stimmt.',
        });
    }
    if (args.archivFehlt || !tag) {
        blocker.push({
            reason: 'kein_archiv',
            message: 'Zu diesem Bericht gibt es keinen Begehungstag im Archiv. Ohne Tag lässt sich nicht buchen — der Eintrag landete sonst auf dem heutigen Datum und damit im falschen Abrechnungszeitraum.',
        });
    }

    // ── Ist der Bericht schon gebucht? ────────────────────────────────────
    //
    // Gefragt wird mit dem Praedikat der SICHT (`isInspectionBilled`), und mit
    // sonst keinem. `isReportBooked` gehoert hier ausdruecklich NICHT her,
    // obwohl es naheliegt: es meldet einen Treffer schon beim blossen
    // `reportRef`, und `book()` schreibt denselben Ref auch an eine reine
    // ZUSATZLEISTUNG. Ausgerechnet der Fall, fuer den die Sicht gebaut ist —
    // „Zusatzleistung gebucht, Begehung nicht" — waere damit gesperrt, mit der
    // falschen Auskunft „bereits gebucht". Der Bearbeiter haette nur noch den
    // Ausweg „nicht abrechenbar" und schloesse eine erbrachte Begehung
    // endgueltig. `isInspectionBilled` schliesst die Zusatzleistungszeile
    // korrekt aus.
    //
    // Bei einer TOCHTER zaehlt allein der Kind-Ref im Protokoll der Mutter —
    // wie in Riegel (2) der Sicht. Das Mutterprotokoll fuehrt eigene Berichte
    // mit eigener Nummerierung; „Bericht #7 abgeschlossen" dort ist der
    // siebte Bericht DER MUTTER und sagt ueber den siebten der Tochter nichts.
    const schonGebucht = row.parent_project_id
        ? (serviceLog || []).some(e => (e as any)?.reportRef === childBookingRef(row.project_id, row.inspection_number))
        : isInspectionBilled(serviceLog as any, row.inspection_number, row.section_id, row.inspection_date, row.parent_project_id);
    if (schonGebucht) {
        blocker.push({
            reason: 'bereits_gebucht',
            message: `Bericht #${row.inspection_number} ist im Protokoll bereits gebucht — vermutlich seit dem Laden der Liste. Es wurde nichts gebucht.`,
        });
    }

    // Mit demselben Netz wie der Schreibpfad (applyReportBookingWithNotices bzw.
    // nachbuchenTochter): eine von der Begehungspauschale gedeckte
    // Begehungsposition wird dort auf die Pauschale umgelenkt. Die Vorschau
    // muss das Ziel zeigen, das WIRKLICH gebucht wird — nicht die Auswahl.
    const zeilen = redirectMainBookingsToFlat(
        resolveMainBookings(
            { ...rebookOpts({ row, bookings: args.bookings, snapshot: args.snapshot, inspectionTime: args.inspectionTime, performerId: '', performerName: '' }) },
            services,
        ),
        services,
    ).rows.map(r => {
        const s = services.find(x => x.id === r.serviceId);
        return {
            serviceId: r.serviceId,
            titel: s?.title || 'Unbekannte Position',
            lvPosition: s?.lvPosition || '—',
            menge: r.quantity,
            einheit: s?.unit || '',
            ...(bookingIsFlatProof(s) ? { nachweis: true } : {}),
        };
    });

    if (zeilen.length === 0) {
        blocker.push({
            reason: 'kein_buchungsziel',
            message: services.length === 0
                ? 'Dieses Projekt hat kein Leistungsverzeichnis — es gibt keine Position, auf die gebucht werden könnte. Zuerst das LV anlegen oder klären, ob die Leistung in einem anderen Projekt abgerechnet wird.'
                : 'Es ist keine Zielposition gewählt. Ohne Position wird nichts gebucht.',
        });
    }

    // Der Tagesriegel wird hier nur ANGEKUENDIGT. Ausgeloest wird er von
    // `applyReportBookingWithNotices`; ihn hier nachzubilden hiesse, ihn
    // zweimal zu pflegen. Angekuendigt gehoert er trotzdem, sonst klickt der
    // Bearbeiter auf „Buchen" und bekommt eine Absage, die er vorher haette
    // lesen koennen.
    const gebuchteNummern = bookedReportNumbers(serviceLog as any);
    const amGleichenTag = (serviceLog || []).find(e => {
        const desc = String(e?.description || '');
        const treffer = /^Bericht #(\d+) abgeschlossen$/.exec(desc);
        if (!treffer || Number(treffer[1]) === row.inspection_number) return false;
        // Derselbe Tag wie im Riegel selbst: `buchungstag` (Europe/Berlin, S5d).
        return buchungstag(e?.date) === tag;
    });
    if (amGleichenTag && gebuchteNummern.size > 0) {
        const nr = /^Bericht #(\d+) abgeschlossen$/.exec(String(amGleichenTag.description || ''))?.[1];
        warnungen.push({
            kind: 'tagesriegel',
            message:
                `Am ${tagDeutsch(tag)} ist bereits Bericht #${nr ?? '?'} gebucht. Der Tagesriegel wird diese Buchung `
                + 'deshalb überspringen — das ist beabsichtigt und hat schon eine Doppelabrechnung verhindert. '
                + 'War es wirklich eine zweite Begehung an diesem Tag, gehört sie über die Leistungserfassung erfasst.',
        });
    }

    if (row.parent_project_id) {
        warnungen.push({
            kind: 'tochter',
            message: args.tagespauschale
                ? 'Tochterprojekt mit TAGESPAUSCHALE: die Buchung geht in das Protokoll des Mutterprojekts. Ist an diesem Tag dort schon eine Begehung gebucht, ist der Tag abgerechnet und es wird nichts hinzugebucht.'
                : 'Tochterprojekt: die Buchung geht in das Protokoll des Mutterprojekts. Eine Aufteilung auf mehrere Lose ist auf diesem Weg gesperrt. Dass Mutter und Tochter am selben Tag begangen wurden, ist bei Einzelabrechnung normal und kein Hindernis.',
        });
    }
    if (row.nur_zusatzleistung_gebucht) {
        warnungen.push({
            kind: 'nur_zusatzleistung',
            message: 'Zu diesem Bericht ist bereits eine ZUSATZLEISTUNG gebucht, nur die Begehung fehlt. Es wird ausschließlich die Begehung nachgebucht.',
        });
    }
    const zeitraum = zeitraumWarnung(args.abrechnung);
    if (zeitraum) warnungen.push(zeitraum);
    if (!args.abrechnung?.stand || args.abrechnung.stand === 'unbekannt') {
        // Schweigen waere hier die gefaehrlichste Auskunft: der Bearbeiter
        // liest ein Fenster ohne Warnung als „geprueft und in Ordnung". Die
        // Aufgabenliste kennt die Abrechnungslage gar nicht — sie steht am
        // Befund des Integritaetswaechters, nicht an der Sicht.
        warnungen.push({
            kind: 'abrechnungslage_unbekannt',
            message:
                'Die Abrechnungslage ist hier NICHT geprüft: es ist unbekannt, ob der Begehungstag bereits '
                + 'auf einer gestellten Rechnung liegt. Vor dem Buchen im Rechnungsmodul nachsehen — liegt der '
                + 'Tag in einem abgerechneten Zeitraum, wäre diese Buchung eine Doppelforderung.',
        });
    }

    const spaeteNennung = nennungAusserhalbFenster(serviceLog, row.inspection_number, tag);
    if (spaeteNennung) warnungen.push(spaeteNennung);

    for (const z of zeilen) {
        const s = services.find(x => x.id === z.serviceId);
        // Eine Nachweiszeile hat keine Menge, also auch keine ungewoehnliche.
        if (s && !z.nachweis && isHourlyUnit(s.unit) && z.menge <= 1) {
            warnungen.push({
                kind: 'menge_ungewoehnlich',
                message: `„${z.titel}" ist eine Stundenposition, gebucht wird aber nur ${z.menge} ${s.unit}. Bitte prüfen, ob die Stunden aus dem Bericht stimmen.`,
            });
        }
    }

    return {
        projektNummer: row.project_number,
        projektName: row.project_name,
        berichtsNummer: row.inspection_number,
        begehungstag: tag,
        uhrzeit: archivUhrzeit(args.snapshot, args.inspectionTime),
        mutterProjektId: row.parent_project_id,
        zeilen,
        blocker,
        warnungen,
    };
};

/**
 * Die Auskunft zur Umlenkung auf die Pauschale (reportBooking,
 * redirectMainBookingsToFlat) — null, wenn nichts umgelenkt wurde.
 *
 * Wer 2.7.10 waehlt und einen Nachweis auf 2.9.1 bekommt, muss das lesen:
 * sonst sucht er die Begehung spaeter auf der Position, die er gewaehlt hat.
 */
export const flatRedirectHinweis = (
    redirect: FlatRedirect | null | undefined,
    services: Array<Pick<ServiceItem, 'id' | 'lvPosition' | 'title'>>,
): string | null => {
    if (!redirect || redirect.fromServiceIds.length === 0) return null;
    const name = (id: string) => {
        const s = services.find(x => x.id === id);
        return s ? [s.lvPosition, s.title].filter(Boolean).join(' ') : id;
    };
    const von = redirect.fromServiceIds.map(name).join(', ');
    return `Gebucht als Nachweis auf der Monatspauschale ${name(redirect.flatServiceId)} statt auf ${von} – der Monat ändert sich nicht.`;
};

/** Darf der Knopf „Buchen" ueberhaupt scharf sein? */
export const istNachbuchbar = (preview: RebookPreview): boolean =>
    preview.blocker.length === 0 && preview.zeilen.length > 0;


/**
 * Darf OHNE Fenster gebucht werden — ein Klick, keine Rueckfrage?
 *
 * Nur wenn nichts zu lesen ist: kein Riegel, eine Zielposition, und KEINE
 * einzige Warnung. Jede Warnung — abgerechneter Zeitraum, unbekannte
 * Abrechnungslage, Tochter, Tagesriegel, spaete Nennung, ungewoehnliche
 * Menge, nur Zusatzleistung — ist ein Satz, den der Bearbeiter vor der
 * Buchung gelesen haben muss. Der Schnellweg darf den Dialog abkuerzen, nicht
 * seine Warnungen. Faellt die Pruefung durch, oeffnet der Aufrufer das Fenster.
 */
export const istSofortBuchbar = (preview: RebookPreview): boolean =>
    istNachbuchbar(preview) && preview.warnungen.length === 0;
